import type { QuizQuestionAward } from "./quizzes";
import type { PolicySelection, CourseRules, AudienceSelection } from "./courseworkPolicy";
/**
 * Assignments API (issue #71, ADR 0002) — the native_basic teaching loop:
 * assignment lifecycle, numbered attempts, and append-only feedback.
 *
 * Typed wrappers over the shared `api` client, mirroring gradebook.ts/
 * courses.ts's shape.
 *
 * Two contract details live in the types rather than in each caller:
 *   - an attempt carries a client-supplied `idempotency_key`. The first request
 *     for a key records the attempt, an exact retry returns that same receipt,
 *     and the same key with different content is refused.
 *   - feedback `visible_at` is three-valued by PRESENCE: omit the key to release
 *     the judgement now, send an explicit `null` to withhold it. `?: string |
 *     null` is deliberate — `undefined` means "release", never "withhold".
 */
import { api } from "@/lib/api/client";
import { commitOptions, retryOptions } from "./commitSeams";
import type { CourseHomeSection, TeachingCourseworkRowV6, LearningCourseworkRowV6 } from "./courseHome";
import type { MaterialLibraryRead } from "./courseMaterials";
import type { JSONContent } from "@tiptap/core";

export type AssignmentStatus = "draft" | "published" | "closed";

/** How an assignment is judged (ADR 0032, amended 2026-10-19). */
export type AssessmentKind = "completion" | "scored" | "external_quiz" | "in_class" | "native_quiz";

/** One shared level of a level rubric: its exact share of each criterion's points. */
export interface RubricLevel {
  id: string;
  name: string;
  /** 0 to 100, distinct across levels; the top level is 100. */
  share: number;
  /** What earns this level for this criterion; optional. */
  evidence?: string;
}

/** One criterion of a rubric; a rubric's points sum to the assignment's points.
 *  A level rubric (S3, migration 214) adds what to look for and the shared levels. */
export interface RubricCriterion {
  id: string;
  label: string;
  points: number;
  description?: string;
  levels?: RubricLevel[];
}

/** Assignment-local simple scale. Draft rows may retain incomplete text/score strings; publication validates them. */
export interface AssignmentScaleRow {
  id: string;
  score: number | string;
  level: string;
  meaning: string;
}
export interface AssignmentScale { rows: AssignmentScaleRow[] }

/**
 * A course material the published brief references (ADR 0039, 2026-11-03).
 * Frozen at publication: later material edits never change it. Drive or the
 * link's host decides who can open it; no Drive id or resource key is sent.
 */
export interface BriefMaterial {
  position: number;
  material_id: string;
  title: string;
  file_url: string;
  google_drive_mime_type: string | null;
  material_type: string | null;
  source: "drive" | "link";
}

export interface Assignment {
  policy?: PolicySelection | null;
  policy_values?: CourseRules | null;
  policy_sources?: Record<string, { kind: "course" | "work"; revision?: number }> | null;
  audience?: AudienceSelection | null;
  opens_at?: string | null;
  published_due_at?: string | null;
  archived_at?: string | null;
  lifecycle_revision?: number;
  current_version_id?: string;
  id: string;
  course_id: string;
  grade_item_id: string | null;
  unit_id: string | null;
  title: string;
  /** What the learner hands in; "none" for a quiz, in-class work or a plain mark of done. */
  expected_deliverable: ExpectedDeliverable | null;
  /** How the work is judged. The server always sends these three; optional here only so fixtures written before them compile. */
  assessment_kind?: AssessmentKind;
  /** The quiz link; external quiz only. */
  external_url?: string | null;
  /** Scored work only. */
  rubric?: RubricCriterion[] | null;
  scale?: AssignmentScale | null;
  instructions: string | null;
  points: number | null;
  due_at: string | null;
  status: AssignmentStatus;
  created_by: string;
  published_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  /** The current version's attached course materials, in brief order; detail reads only. */
  materials?: BriefMaterial[];
}

export interface AssignmentVersion {
  id: string;
  version_no: number;
  title: string;
  expected_deliverable: ExpectedDeliverable | null;
  assessment_kind?: AssessmentKind;
  external_url?: string | null;
  rubric?: RubricCriterion[] | null;
  scale?: AssignmentScale | null;
  instructions: string | null;
  points: number | null;
  due_at: string | null;
  effective_from: string;
  actor_user_id: string;
  reason: string | null;
  created_at: string;
}

/**
 * A learner's figure for the assignment's grade line: the mean of the line's
 * released pieces, how many there are, and how many past-due pieces have nothing
 * recorded ("1 not handed in, not counted yet"). Null mean: nothing released yet.
 */
export interface LineFigure {
  mean: number | null;
  released_count: number;
  not_handed_in_count: number;
  /** The grade line's name ("Quizzes"); the same word the teaching team sees. */
  label: string | null;
}

/**
 * The terms of the version an attempt answered. A judgement is applied against these,
 * and a correction can change points, kind or rubric after hand-ins, so a score reads
 * against its own attempt's terms and never the work's current ones.
 */
export interface AttemptTerms {
  policy_values?: CourseRules | null;
  policy_sources?: Assignment["policy_sources"];
  version_id: string;
  assessment_kind: AssessmentKind;
  points: number | null;
  rubric: RubricCriterion[] | null;
  scale?: AssignmentScale | null;
}

/** GET /assignments/{id} — the assignment plus every set of terms it has had. */
export interface AssignmentDetail extends Assignment {
  window?: { reason?: "opens_later" | "deadline_passed" | "open"; open?: boolean; due_at: string | null; accepts_until: string | null; opens_at: string | null; extension_id: string | null } | null;
  versions: AssignmentVersion[];
  /** The database's word: the due time has passed. Never compared with the browser's clock. */
  past_due?: boolean;
  /** A learner's own figure only; absent for the teaching team, null with no line. */
  my_line?: LineFigure | null;
  /** Who marks and who releases: staffing and release grants, never marking progress. */
  release_line?: ReleaseLine | null;
}

/** One teaching team member as a learner reads them; `name` is null until the person has one. */
export interface ReleaseLinePerson { role: "primary" | "co_instructor"; name: string | null }
export interface ReleaseLine { markers: ReleaseLinePerson[]; releasers: ReleaseLinePerson[] }

/** One exact held judgement chosen for a release round. */
export interface ReleaseChoice { submission_id: string; feedback_id: string }
export interface CreateRelease { expected_updated_at?: string | null; round: number; items: ReleaseChoice[] }
export interface ReleasedItem {
  submission_id: string; held_feedback_id: string; released_feedback_id: string;
  /** `primary_cover`: the primary released a granted marker's completion work in their place. */
  authority: "primary" | "primary_cover" | "granted_marker"; cover_for_user_id: string | null;
  /** The released attempt's learner and number, so a round still names an attempt a later one replaced. */
  enrollment_id?: string; attempt_number?: number | null;
}
/** A learner's latest attempt the round left out, by id only. */
export interface ReleaseLeftOut { enrollment_id: string; submission_id: string; attempt_number: number | null; reason: "not_marked" | "held_not_chosen" }
export type ReleaseResult = {
  state: "confirmed"; assignment_id: string; release_id: string; round: number; replayed: boolean;
  included: ReleasedItem[]; not_included: ReleaseLeftOut[];
} & HeldAccount;
/**
 * Learners (enrollment ids) whose released quiz result is held from them while their
 * replacement is to come, and whether a released result here shows answers (null when
 * none is held). Absent on receipts written before it existed.
 */
export interface HeldAccount { held?: string[]; shows_answers?: boolean | null }
/** One release round as it was made; `actor_name` is null while the releaser has no name. */
export interface ReleaseRound { release_id: string; round: number; actor_user_id: string; actor_name: string | null; created_at: string; items: ReleasedItem[]; not_included: ReleaseLeftOut[] }
export type ReleaseRounds = {
  assignment_id: string; next_round: number;
  /** Each round as it was made, with the exclusions recorded when it was made. */
  rounds: ReleaseRound[];
  /** Unrevoked grants; `live` is false once the holder's teaching assignment ended, and the primary can still revoke it. */
  grants: { grantee_user_id: string; granted_by: string; granted_at: string; teaching_assignment_id: string; live: boolean }[];
} & HeldAccount;

export interface CreateAssignment {
  title: string;
  expected_deliverable: ExpectedDeliverable;
  /** Omitted: scored where points are stated, else completion. */
  assessment_kind?: AssessmentKind;
  external_url?: string | null;
  rubric?: RubricCriterion[] | null;
  scale?: AssignmentScale | null;
  instructions?: string | null;
  points?: number | null;
  due_at?: string | null;
  grade_item_id?: string | null;
  unit_id?: string | null;
}

/**
 * A change to an assignment's terms appends a version rather than editing.
 *
 * Published terms change only with a `reason` (a correction learners never
 * see); a draft's edits need none, because no learner has read them. The
 * grade item is a fact of the draft: `grade_item_id` (an explicit null
 * clears it) is refused once the work is published. `expected_updated_at` is
 * the version the edit was made against; a newer one answers 409 with the
 * assignment as it now stands in `current`.
 */
export interface CorrectAssignment {
  /** "none" is sent through `CorrectAssignmentRequest`; this stays narrow like the read types. */
  expected_deliverable?: SubmissionKind;
  /** A kind change carries what it implies: the deliverable "none" for a quiz or
   *  in-class work, and no link or rubric the old kind left behind. */
  assessment_kind?: AssessmentKind;
  external_url?: string | null;
  rubric?: RubricCriterion[] | null;
  scale?: AssignmentScale | null;
  title?: string;
  instructions?: string | null;
  points?: number | null;
  due_at?: string | null;
  unit_id?: string | null;
  grade_item_id?: string | null;
  reason?: string;
  expected_updated_at?: string;
}

/** What `assignmentsApi.correct` accepts: a correction that may also choose "none". */
export type CorrectAssignmentRequest = Omit<CorrectAssignment, "expected_deliverable"> & {
  expected_deliverable?: ExpectedDeliverable;
};

export interface CorrectedAssignment extends Assignment {
  version: { id: string; version_no: number };
}

export type SubmissionKind = "link" | "text" | "artifact";

/** What an attempt holds: a hand-in, or no content at all (marked by the learner, recorded by the teacher). */
export type ContentKind = SubmissionKind | "attested" | "recorded" | "quiz";

/** The deliverable a teacher chooses; "none" for work with nothing to hand in. */
export type ExpectedDeliverable = SubmissionKind | "none";

/** How an attempt reached IMAM: the learner's hand-in, their own mark of taken or
 *  done, or a record the teaching team made for them. */
export type AttemptKind = "handed_in" | "attested" | "recorded";

/** ADR 0002's learner-facing states, derived by the server from the attempt and
 *  its latest VISIBLE feedback. */
export type SubmissionState = "submitted" | "returned" | "resubmitted" | "graded";

export interface SubmissionArtifact {
  id: string;
  checksum: string;
  content_type: string;
  byte_size: number;
  original_name: string | null;
  retention_class: string;
}

/** The one receipt shape, returned by every submission path. */
export interface Submission {
  id: string;
  course_id: string;
  assignment_id: string;
  assignment_version_id: string;
  enrollment_id: string;
  /** The learner's own attempt number; null for a Missing mark, which takes none. */
  attempt_number: number | null;
  /** "attested" and "recorded" attempts carry no content; `attempt_kind` says how it arrived. */
  content_kind: ContentKind;
  link_url: string | null;
  content_text: string | null;
  submitted_at: string;
  submitted_by: string;
  due_at_snapshot: string | null;
  payload_digest: string;
  late: boolean;
  artifact: SubmissionArtifact | null;
  state: SubmissionState;
  /** How it arrived. Always sent; optional here only so fixtures written before it compile. */
  attempt_kind?: AttemptKind;
  /** The recorder, for a recorded attempt only. */
  recorded_by?: string | null;
  /** The terms this attempt answered. Always sent; null only if the version cannot be read. */
  attempt_terms?: AttemptTerms | null;
  document_snapshot?: JSONContent | null;
  document_schema_version?: 1 | null;
  document_digest?: string | null;
  source_draft_id?: string | null;
  source_draft_revision?: number | null;
  parent_submission_id?: string | null;
  return_event_id?: string | null;
}

export interface CreateSubmission {
  assignment_version_id?: string;
  content_kind: SubmissionKind;
  idempotency_key: string;
  link_url?: string | null;
  content_text?: string | null;
}

export interface ReserveArtifactUpload {
  assignment_version_id?: string;
  idempotency_key: string;
  checksum: string;
  content_type: string;
  byte_size: number;
  original_name: string;
}

/** The tenant's typed upload policy (owner decision D8); reservation enforces the same terms. */
export interface ArtifactUploadPolicy {
  max_bytes: number;
  summary: string;
  types: { content_type: string; extensions: string[] }[];
}

/** Object metadata read without settling the reservation; only hand-in verifies the bytes. */
export type ArtifactUploadArrival = "arrived" | "missing" | "mismatch" | "expired" | "discarded" | "finalized";

export interface ArtifactUploadReservation {
  reservation_id: string;
  state: "pending" | "finalized";
  upload_url?: string;
  expires_at?: string;
  submission_id?: string;
}

/** `complete` and `missing` close an attempt as `graded` does; to a learner the
 *  attempt reads as graded, and the outcome says which it was. */
export type FeedbackOutcome = "returned" | "graded" | "complete" | "missing";

/** {criterion id: score} for a rubric grade; each within its criterion's points. */
export type CriterionScores = Record<string, number>;

/** A level mark's evidence for one criterion; the server derives the baseline. */
export interface CriterionLevel {
  level_id: string;
  baseline: number;
  awarded: number;
  /** Only when the award differs from the level's points. */
  reason: string | null;
}

export interface Feedback {
  id: string;
  submission_id: string;
  event_no: number;
  outcome: FeedbackOutcome;
  body: string | null;
  score: number | null;
  criterion_scores?: CriterionScores | null;
  selected_scale_row_id?: string | null;
  criterion_levels?: Record<string, CriterionLevel> | null;
  quiz_awards?: Record<string, QuizQuestionAward> | null;
  actor_user_id: string;
  visible_at: string | null;
  created_at: string;
  payload_digest: string;
}

export interface CreateFeedback {
  outcome: FeedbackOutcome;
  body?: string | null;
  score?: number | null;
  /** A rubric grade scores every criterion; the score is their sum. */
  criterion_scores?: CriterionScores | null;
  selected_scale_row_id?: string | null;
  visible_at?: string | null;
}

/** One learner in a batch: an existing attempt, or an enrolled learner with none
 *  (for whom the server writes the recorded attempt and the judgement together). */
export type JudgementItem = {
  review_automatic?: boolean;
  expected_feedback_id?: string | null;
  outcome?: FeedbackOutcome;
  body?: string | null;
  score?: number | null;
  criterion_scores?: CriterionScores | null;
  selected_scale_row_id?: string | null;
} & ({ submission_id: string; enrollment_id?: never } | { enrollment_id: string; submission_id?: never });

/** One marker's mark of one attempt, recorded held under the judgement on record. */
export interface RecordMark {
  quiz_awards?: Record<string, { points: number; reason: string }>;
  expected_feedback_id: string | null;
  decision: "graded" | "returned" | "complete";
  levels?: Record<string, string>;
  /** Only criteria awarded other than their level's points, each with its own reason. */
  awards?: Record<string, { points: number; reason: string }>;
  comment?: string | null;
  draft_revision?: number | null;
}

export interface MarkResult {
  state: "confirmed";
  submission_id: string;
  held: true;
  feedback: Feedback;
}

/** A marker's private draft of one attempt; every save is a journalled revision. */
export interface MarkDraft {
  id: string;
  submission_id: string;
  revision: number;
  recorded_revision: number | null;
  recorded_feedback_id: string | null;
  recorded: boolean;
  saved_at: string;
  document: Record<string, unknown>;
}

/** One release choice for the whole act; `outcome` is the default for items that name none. */
export interface BatchJudgements {
  expected_updated_at?: string;
  retry_ids?: string[];
  outcome?: FeedbackOutcome;
  /** True shows every judgement to its learner now; false withholds them all. */
  release?: boolean;
  items: JudgementItem[];
}

export type JudgementResult = "recorded_released" | "recorded_not_released" | "not_recorded";

export interface JudgementItemResult {
  index: number;
  submission_id: string | null;
  enrollment_id: string | null;
  result: JudgementResult;
  /** Why it was not recorded; null otherwise. */
  reason: string | null;
  feedback: Feedback | null;
}

/** One outcome per item, never a single success for all. */
export interface BatchJudgementsResult {
  assignment_id: string;
  release: boolean;
  counts: Record<JudgementResult, number>;
  items: JudgementItemResult[];
}

/** A judgement as the teaching team's Review reads it (standing and released). */
export interface JudgementView {
  id: string;
  event_no: number;
  outcome: FeedbackOutcome;
  body: string | null;
  score: number | null;
  criterion_scores: CriterionScores | null;
  criterion_levels?: Record<string, CriterionLevel> | null;
  quiz_awards?: Record<string, QuizQuestionAward> | null;
  selected_scale_row_id?: string | null;
  visible_at: string | null;
  created_at: string;
  /** When it was recorded: a released copy keeps its held record's time. */
  recorded_at?: string;
  actor_user_id: string;
}

/** One enrolled learner in GET /assignments/{id}/review (teaching team only). */
export interface ReviewLearnerRow {
  automatic_review?: { points: number | string | null; has_private_draft: boolean } | null;
  enrollment_id: string;
  student_id: string;
  sid: string | null;
  display_name: string;
  /** Current roster status, or withdrawn for an explicitly addressed historical attempt. */
  status?: "enrolled" | "incomplete" | "complete" | "withdrawn";
  /** False when the enrollment takes no new marks: a completed learner's work is read-only. */
  markable?: boolean;
  attempt_count: number;
  attempt: Submission | null;
  /** The terms the latest attempt answered: render and judge against these, not the work's current terms. */
  attempt_terms?: AttemptTerms | null;
  /** The newest judgement of the latest attempt, released or not. */
  standing: JudgementView | null;
  /** The newest judgement the learner can see. */
  released: JudgementView | null;
  /** Past due with no attempt of any kind. */
  not_handed_in: boolean;
  line: LineFigure | null;
}

/** A recipient whose enrollment was withdrawn: kept on the work's record, read-only (S2A-1). */
export type FormerRecipientRow = Pick<ReviewLearnerRow, "enrollment_id" | "student_id" | "sid" | "display_name" | "attempt_count" | "attempt" | "standing" | "released"> & { status: "withdrawn" };

export interface AssignmentReview {
  scale?: AssignmentScale | null;
  /** Filtered scope from the same authorized read; all rows retain selection and drafts. */
  visible_ids?: string[];
  expected_updated_at?: string;
  assignment_id: string;
  assessment_kind: AssessmentKind;
  grade_item_id: string | null;
  /** The database's word: the due time has passed. Never compared with the browser's clock. */
  past_due?: boolean;
  /** D13: this actor may release this work now; otherwise Review records held. */
  may_release?: boolean;
  /** Who releases this work (staffing and grants). */
  releasers?: ReleaseLinePerson[];
  rows: ReviewLearnerRow[];
  /** Withdrawn recipients of this work; never marked or released from here. */
  former?: FormerRecipientRow[];
}

export interface WritingDraft {
  erased_at?: string | null;
  id: string;
  assignment_id: string;
  enrollment_id: string;
  cycle: number;
  parent_submission_id: string | null;
  return_event_id: string | null;
  review_return_event_id?: string | null;
  revision: number;
  document: JSONContent;
  schema_version: 1;
  content_text: string;
  content_digest: string;
  saved_at: string;
  submitted_id: string | null;
}
export interface WritingSave {
  operation_id: string;
  expected_revision: number;
  document: JSONContent;
}
export interface WritingReview {
  operation_id: string;
  draft_id: string;
  expected_revision: number;
  content_digest: string;
  assignment_version_id: string;
  expected_return_event_id?: string | null;
}
export type ReferenceDocument = { type: "link"; url: string; title?: string } | { type: "artifact"; reservation_id: string | null };
export interface ReferenceDraft extends Omit<WritingDraft, "schema_version" | "document"> {
  schema_version: 2;
  document: ReferenceDocument;
  upload: null | { reservation_id: string; state: string; original_name: string; checksum: string; content_type: string; byte_size: number; expires_at: string; assignment_version_id: string };
}
export interface ReferenceSave extends Omit<WritingSave, "document"> { document: ReferenceDocument }
export type ReferenceFinalization = Pick<WritingReview, "draft_id" | "expected_revision" | "content_digest" | "expected_return_event_id">;
export interface NativeCourseworkRead {
  coursework: CourseHomeSection<{ items: (TeachingCourseworkRowV6 | LearningCourseworkRowV6)[] }>;
  read_at: string;
  visible_ids: string[];
  ranking: MaterialLibraryRead["ranking"];
}
export const assignmentsApi = {
  readReference: (id: string) => api.get<{ draft: ReferenceDraft | null; upload_policy?: ArtifactUploadPolicy | null }>(`/assignments/${id}/reference-draft`),
  startReference: (id: string, kind: "link" | "artifact") => api.post<ReferenceDraft>(`/assignments/${id}/reference-draft`, { kind }),
  saveReference: (id: string, draftId: string, body: ReferenceSave) => api.put<Pick<ReferenceDraft, "revision" | "saved_at" | "content_digest">>(`/assignments/${id}/reference-drafts/${draftId}`, body),
  confirmReferenceSave: (id: string, draftId: string, body: ReferenceSave) => api.post<{ acknowledged: boolean; revision?: number; saved_at?: string; content_digest?: string }>(`/assignments/${id}/reference-drafts/${draftId}/saved-operation`, body),
  submitReference: (id: string, body: WritingReview, key: string) => api.post<Submission>(`/assignments/${id}/reference-draft/submit`, body, commitOptions(key)),
  getCoursework: (courseId: string, perspective: "teaching" | "learning", query = "", archived = false) => api.get<NativeCourseworkRead>(`/courses/${courseId}/coursework-read?perspective=${perspective}&archived=${archived}${query ? `&q=${encodeURIComponent(query)}` : ""}`),
  readWriting: (id: string) => api.get<{ draft: WritingDraft | null }>(`/assignments/${id}/writing`),
  startWriting: (id: string) => api.post<WritingDraft>(`/assignments/${id}/writing/start`),
  confirmWritingSave: (assignmentId: string, draftId: string, body: WritingSave) => api.post<{ acknowledged: boolean; revision?: number; content_digest?: string; saved_at?: string }>(`/assignments/${assignmentId}/writing/${draftId}/saved-operation`, body),
  saveWriting: (id: string, draftId: string, body: WritingSave) => api.put<Pick<WritingDraft, "revision" | "saved_at" | "content_digest">>(`/assignments/${id}/writing/${draftId}`, body),
  submitWriting: (id: string, body: WritingReview, key = `writing:${body.operation_id}`) => api.post<Submission>(`/assignments/${id}/writing/submit`, body, commitOptions(key)),
  list: (courseId: string) => api.get<Assignment[]>(`/courses/${courseId}/assignments`),
  create: (courseId: string, body: CreateAssignment, key?: string) =>
    key ? api.post<Assignment>(`/courses/${courseId}/assignments`, body, commitOptions(key)) : api.post<Assignment>(`/courses/${courseId}/assignments`, body),
  get: (assignmentId: string) => api.get<AssignmentDetail>(`/assignments/${assignmentId}`),
  correct: (assignmentId: string, body: CorrectAssignmentRequest, key?: string) =>
    key ? api.patch<CorrectedAssignment>(`/assignments/${assignmentId}`, body, commitOptions(key)) : api.patch<CorrectedAssignment>(`/assignments/${assignmentId}`, body),
  publish: (assignmentId: string, key?: string, expected_updated_at?: string) => key ? api.post<Assignment>(`/assignments/${assignmentId}/publish`, { expected_updated_at }, commitOptions(key)) : api.post<Assignment>(`/assignments/${assignmentId}/publish`),
  close: (assignmentId: string, key?: string, expected_updated_at?: string) => key ? api.post<Assignment>(`/assignments/${assignmentId}/close`, { expected_updated_at }, commitOptions(key)) : api.post<Assignment>(`/assignments/${assignmentId}/close`),
  /** Only a draft is removed; published work is closed instead. */
  remove: (assignmentId: string, key?: string, expected_updated_at?: string) => key ? api.delete<void>(`/assignments/${assignmentId}`, { expected_updated_at }, commitOptions(key)) : api.delete<void>(`/assignments/${assignmentId}`),

  submit: (assignmentId: string, body: CreateSubmission) =>
    api.post<Submission>(`/assignments/${assignmentId}/submissions`, body, commitOptions(body.idempotency_key)),
  listSubmissions: (assignmentId: string) =>
    api.get<Submission[]>(`/assignments/${assignmentId}/submissions`),
  /** Mark as taken (external quiz) or Mark done (completion with nothing to hand in):
   *  the learner's own attempt, no content; a repeat returns the same attempt. */
  attest: (assignmentId: string, key?: string, expected_updated_at?: string) =>
    key ? api.post<Submission>(`/assignments/${assignmentId}/attempts/attested`, { expected_updated_at }, commitOptions(key)) : api.post<Submission>(`/assignments/${assignmentId}/attempts/attested`),
  /** The teaching team's Review: one row per enrolled learner. */
  review: (assignmentId: string, query = "", submissionId?: string) => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (submissionId) params.set("submission_id", submissionId);
    return api.get<AssignmentReview>(`/assignments/${assignmentId}/review${params.size ? `?${params}` : ""}`);
  },
  /** Judge many in one act; each item is its own record with its own outcome. */
  judgeBatch: (assignmentId: string, body: BatchJudgements, key?: string, retry = false) =>
    key ? api.post<BatchJudgementsResult>(`/assignments/${assignmentId}/judgements`, body, retry ? retryOptions(key) : commitOptions(key)) : api.post<BatchJudgementsResult>(`/assignments/${assignmentId}/judgements`, body),
  /** This marker's own private draft of an attempt, or none. */
  markDraft: (submissionId: string) => api.get<{ draft: MarkDraft | null }>(`/submissions/${submissionId}/mark-draft`),
  saveMarkDraft: (submissionId: string, body: { expected_revision: number; document: Record<string, unknown> }) =>
    api.put<{ draft: MarkDraft }>(`/submissions/${submissionId}/mark-draft`, body),
  /** Record this marker's mark, held; release is a separate act. */
  recordMark: (submissionId: string, body: RecordMark, key: string) =>
    api.post<MarkResult>(`/submissions/${submissionId}/marks`, body, commitOptions(key)),
  /** Release rounds so far and the round number the next release sends. */
  releaseRounds: (assignmentId: string) => api.get<ReleaseRounds>(`/assignments/${assignmentId}/releases`),
  /** Release exactly the chosen held judgements as one round; the same key and round replay, never widen. */
  release: (assignmentId: string, body: CreateRelease, key: string) =>
    api.post<ReleaseResult>(`/assignments/${assignmentId}/releases`, body, commitOptions(key)),
  getSubmission: (submissionId: string) => api.get<Submission>(`/submissions/${submissionId}`),
  reserveArtifactUpload: (assignmentId: string, body: ReserveArtifactUpload) =>
    api.post<ArtifactUploadReservation>(`/assignments/${assignmentId}/artifact-uploads`, body),
  finalizeArtifactUpload: (assignmentId: string, reservationId: string, key?: string, review?: ReferenceFinalization) =>
    key ? api.post<Submission>(`/assignments/${assignmentId}/artifact-uploads/${reservationId}/finalize`, review, commitOptions(key)) : api.post<Submission>(`/assignments/${assignmentId}/artifact-uploads/${reservationId}/finalize`),
  readArtifactUploadArrival: (assignmentId: string, reservationId: string) =>
    api.get<{ reservation_id: string; arrival: ArtifactUploadArrival; read_at: string }>(`/assignments/${assignmentId}/artifact-uploads/${reservationId}/arrival`),
  abandonArtifactUpload: (assignmentId: string, reservationId: string) =>
    api.post<{ reservation_id: string; state: string }>(
      `/assignments/${assignmentId}/artifact-uploads/${reservationId}/abandon`,
    ),
  downloadArtifact: (artifactId: string) =>
    api.post<{ download_url: string; expires_in_seconds: number }>(`/submission-artifacts/${artifactId}/download`),

  createFeedback: (submissionId: string, body: CreateFeedback) =>
    api.post<Feedback>(`/submissions/${submissionId}/feedback`, body),
  listFeedback: (submissionId: string) =>
    api.get<Feedback[]>(`/submissions/${submissionId}/feedback`),
};
